/**
 * Ticket to Talk — vanilla TS UI (multi-class + game-like chips)
 * Modes: Full mock + Section drill. Local-first IndexedDB.
 */

import type {
  AttemptDraft,
  AttemptResult,
  ChoiceLetter,
  ClassKey,
  PoolFile,
  Question,
} from "./types.js";
import {
  CLASS_CONFIGS,
  CLASS_STORAGE_KEY,
  configForPool,
  parseClassKey,
  passScoreForPool,
} from "./types.js";
import {
  createMockAttempt,
  createDrillAttempt,
  submitAttempt,
  getQuestion,
  activeGroupsInPool,
  subelementsFromPool,
} from "./score-engine.js";
import {
  ensureProfile,
  saveAttempt,
  exportLocalProgress,
  downloadJson,
  setPreferredClass,
  readStoredClass,
  computeHomeStats,
  type HomeStats,
} from "./idb.js";

/** Optional short labels when syllabus titles are known (Tech). Others use id. */
const SUBELEMENT_LABELS: Record<string, string> = {
  T1: "T1 — Commission’s Rules",
  T2: "T2 — Operating Procedures",
  T3: "T3 — Radio Wave Propagation",
  T4: "T4 — Amateur Radio Practices",
  T5: "T5 — Electrical Principles",
  T6: "T6 — Electronic Components",
  T7: "T7 — Practical Circuits",
  T8: "T8 — Signals and Emissions",
  T9: "T9 — Antennas and Feed Lines",
  T0: "T0 — Safety",
  G1: "G1 — Commission’s Rules",
  G2: "G2 — Operating Procedures",
  G3: "G3 — Radio Wave Propagation",
  G4: "G4 — Amateur Radio Practices",
  G5: "G5 — Electrical Principles",
  G6: "G6 — Circuit Components",
  G7: "G7 — Practical Circuits",
  G8: "G8 — Signals and Emissions",
  G9: "G9 — Antennas and Feed Lines",
  G0: "G0 — Electrical and RF Safety",
  E1: "E1 — Commission’s Rules",
  E2: "E2 — Operating Procedures",
  E3: "E3 — Radio Wave Propagation",
  E4: "E4 — Amateur Practices",
  E5: "E5 — Electrical Principles",
  E6: "E6 — Circuit Components",
  E7: "E7 — Practical Circuits",
  E8: "E8 — Signals and Emissions",
  E9: "E9 — Antennas and Propagation",
  E0: "E0 — Safety",
};

type Session = {
  mode: "full_mock" | "section_drill";
  draft: AttemptDraft;
  pool: PoolFile;
  startedAt: string;
  index: number;
  answers: Record<string, ChoiceLetter>;
  hintUsed: boolean;
  hintUsedByQuestion: Record<string, boolean>;
  /** Drill: whether current question already showed feedback */
  revealed: boolean;
  /** Consecutive correct answers this session (reset on miss). */
  streak: number;
  /** Best streak reached this session. */
  bestStreak: number;
};

let pool: PoolFile | null = null;
let activeClass: ClassKey = "technician";
let session: Session | null = null;
let lastResult: AttemptResult | null = null;
let homeStats: HomeStats | null = null;
/** Session-only fallback when IDB unavailable */
let sessionOnlyStreak = 0;

const app = () => document.getElementById("ttt-app")!;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") node.className = v;
    else if (k === "text") node.textContent = v;
    else if (
      k.startsWith("aria-") ||
      k === "role" ||
      k === "type" ||
      k === "href" ||
      k === "for" ||
      k === "id" ||
      k === "value" ||
      k === "disabled" ||
      k === "hidden" ||
      k === "title"
    ) {
      node.setAttribute(k, v);
    } else {
      node.setAttribute(k, v);
    }
  }
  for (const c of children) {
    node.append(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return node;
}

function seedNow(): string {
  return `ttt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function poolUrlFor(classKey: ClassKey): string {
  return new URL(CLASS_CONFIGS[classKey].poolPath, window.location.href).href;
}

async function loadPool(classKey: ClassKey): Promise<PoolFile> {
  const res = await fetch(poolUrlFor(classKey));
  if (!res.ok) throw new Error(`Pool load failed (${classKey}): ${res.status}`);
  const data = (await res.json()) as PoolFile;
  if (!data.questions?.length) throw new Error("Pool pack empty");
  return data;
}

function subelementOptions(p: PoolFile): { id: string; label: string }[] {
  return subelementsFromPool(p).map((id) => ({
    id,
    label: SUBELEMENT_LABELS[id] ?? id,
  }));
}

function sessionScore(s: Session): { correct: number; answered: number } {
  let correct = 0;
  let answered = 0;
  for (const item of s.draft.items) {
    const sel = s.answers[item.questionId];
    if (!sel) continue;
    answered += 1;
    const q = getQuestion(s.pool, item.questionId);
    if (q && sel === q.answer) correct += 1;
  }
  return { correct, answered };
}

/** Recompute consecutive-correct streak (reset on miss). */
function recomputeStreak(s: Session): void {
  let streak = 0;
  let best = s.bestStreak;
  const limit =
    s.mode === "section_drill"
      ? s.revealed
        ? s.index
        : s.index - 1
      : s.draft.items.length - 1;

  for (let i = 0; i <= limit; i++) {
    if (i < 0) continue;
    const item = s.draft.items[i]!;
    const sel = s.answers[item.questionId];
    if (!sel) {
      streak = 0;
      continue;
    }
    const q = getQuestion(s.pool, item.questionId);
    if (q && sel === q.answer) {
      streak += 1;
      if (streak > best) best = streak;
    } else {
      streak = 0;
    }
  }
  s.streak = streak;
  s.bestStreak = best;
  sessionOnlyStreak = Math.max(sessionOnlyStreak, best);
}

function updateDocMeta(p: PoolFile, classKey: ClassKey): void {
  const cfg = CLASS_CONFIGS[classKey];
  const title = `Ticket to Talk — ${cfg.licenseClass} exam practice — Get On The Air`;
  document.title = title;
  const desc = document.querySelector('meta[name="description"]');
  if (desc) {
    desc.setAttribute(
      "content",
      `Practice FCC ${cfg.elementLabel} (${cfg.licenseClass}) full mocks and section drills in your browser. Local-first. Free practice — no paywall.`,
    );
  }
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.setAttribute("content", `Ticket to Talk — ${cfg.licenseClass} exam practice`);
  const foot = document.getElementById("ttt-footer-blurb");
  if (foot) {
    foot.innerHTML =
      `<strong>Ticket to Talk</strong> — ${cfg.licenseClass} (${cfg.elementLabel}) practice is <strong>free</strong>: full pool, unlimited mocks &amp; drills, local progress. ` +
      `Technician path stays free forever; General and Extra are free for now too. No account required.`;
  }
  const attr = document.getElementById("ttt-attribution");
  if (attr) {
    attr.textContent = p.attribution || "";
    attr.hidden = !p.attribution;
  }
}

function makeClassPicker(onPick: (key: ClassKey) => void): HTMLElement {
  const wrap = el("div", {
    class: "ttt-class-picker",
    role: "group",
    "aria-label": "License class",
  });
  const keys: ClassKey[] = ["technician", "general", "extra"];
  for (const key of keys) {
    const cfg = CLASS_CONFIGS[key];
    const btn = el(
      "button",
      {
        type: "button",
        class: `ttt-chip ttt-class-chip${activeClass === key ? " is-active" : ""}`,
        "aria-pressed": activeClass === key ? "true" : "false",
        id: `ttt-class-${key}`,
      },
      [cfg.licenseClass],
    );
    btn.addEventListener("click", () => onPick(key));
    wrap.append(btn);
  }
  return wrap;
}

async function switchClass(key: ClassKey): Promise<void> {
  if (key === activeClass && pool) {
    renderHome();
    return;
  }
  const root = app();
  root.replaceChildren(el("p", { class: "legal-note", text: "Loading pool…" }));
  try {
    activeClass = key;
    await setPreferredClass(key);
    pool = await loadPool(key);
    await ensureProfile(pool.poolVersion, key);
    homeStats = await computeHomeStats(pool.poolVersion, CLASS_CONFIGS[key].element).catch(
      () => null,
    );
    updateDocMeta(pool, key);
    renderHome();
  } catch (err) {
    renderError(err instanceof Error ? err.message : String(err));
  }
}

function renderHome(): void {
  const root = app();
  root.replaceChildren();
  if (!pool) return;

  const cfg = configForPool(pool);
  const activeCount = pool.questions.filter((q) => q.withdrawn !== true).length;
  const fixtureCount = pool.questions.filter((q) => (q.tags ?? []).includes("fixture")).length;
  const poolNote =
    fixtureCount > 0
      ? ` · ${pool.questionCount} items (${activeCount - fixtureCount} NCVEC, ${fixtureCount} fixture)`
      : ` · ${activeCount} active NCVEC questions · ${cfg.mockTotal} groups`;

  const banner = el("div", { class: "ttt-banner", role: "status" }, [
    el("strong", {}, [`${cfg.licenseClass} · Pool ${pool.poolVersion}`]),
    document.createTextNode(
      ` · rev ${pool.revision} · effective ${pool.effectiveFrom} → ${pool.effectiveTo}${poolNote}`,
    ),
  ]);

  const picker = makeClassPicker((key) => {
    void switchClass(key);
  });

  const statsBits: string[] = [];
  if (homeStats?.bestMockScore != null && homeStats.bestMockTotal != null) {
    statsBits.push(`Best mock ${homeStats.bestMockScore}/${homeStats.bestMockTotal}`);
  }
  if (homeStats && homeStats.sessionCount > 0) {
    statsBits.push(`${homeStats.sessionCount} session${homeStats.sessionCount === 1 ? "" : "s"}`);
  }
  if (homeStats && homeStats.bestStreak > 0) {
    statsBits.push(`Best streak ${homeStats.bestStreak}`);
  } else if (sessionOnlyStreak > 0) {
    statsBits.push(`Session streak ${sessionOnlyStreak}`);
  }

  const heroKids: (Node | string)[] = [
    el("div", {
      class: "eyebrow",
      text: `Games · Ticket to Talk · ${cfg.elementLabel}`,
    }),
    el("h1", { text: "Practice until the ticket is punched." }),
    el("p", { class: "lede" }, [
      `Full mocks mirror the VE booklet (${cfg.mockTotal} questions, pass at ${cfg.passScore}). ` +
        `Section drills give you ten focused reps and one retransmit hint. ` +
        `Everything stays in this browser — free practice for Technician, General, and Extra.`,
    ]),
    picker,
  ];
  if (statsBits.length) {
    heroKids.push(
      el("div", { class: "ttt-home-stats", role: "status" }, [
        ...statsBits.map((t, i) => {
          const chip = el("span", { class: "ttt-chip ttt-stat-chip", text: t });
          if (i > 0) return chip;
          return chip;
        }),
      ]),
    );
  }

  const hero = el("section", { class: "ttt-hero" }, heroKids);

  const modes = el("section", { class: "ttt-modes row two stack" }, []);

  const mockCard = el("article", { class: "card ttt-card" }, [
    el("div", { class: "kicker", text: "Exam dress rehearsal" }),
    el("h2", { text: "Full mock" }),
    el("p", {}, [
      `${cfg.mockTotal} questions — exactly one per syllabus group. No hints. No per-question reveal until you submit. Pass ≥ ${cfg.passScore}.`,
    ]),
    el("div", { class: "actions" }, [
      (() => {
        const b = el("button", { type: "button", class: "btn", id: "ttt-start-mock" }, [
          "Start full mock",
        ]);
        b.addEventListener("click", () => startMock());
        return b;
      })(),
    ]),
  ]);

  const drillSelect = el("select", {
    id: "ttt-subelement",
    "aria-label": "Subelement for drill",
  });
  for (const s of subelementOptions(pool)) {
    drillSelect.append(el("option", { value: s.id, text: s.label }));
  }

  const sampleSub = subelementOptions(pool)[0]?.id ?? "T1";
  const sampleGroup = configForPool(pool).groups[0] ?? "T1A";

  const drillCard = el("article", { class: "card ttt-card" }, [
    el("div", { class: "kicker", text: "Topic focus" }),
    el("h2", { text: "Section drill" }),
    el("p", {}, [
      "10 questions from one subelement. Immediate feedback + explanation. One retransmit hint for the session (shows hint text, never the letter).",
    ]),
    el("label", { for: "ttt-subelement", class: "ttt-label", text: "Subelement" }),
    drillSelect,
    el("div", { class: "actions", style: "margin-top:0.8rem" }, [
      (() => {
        const b = el("button", { type: "button", class: "btn", id: "ttt-start-drill" }, [
          "Start section drill",
        ]);
        b.addEventListener("click", () => {
          const id = (document.getElementById("ttt-subelement") as HTMLSelectElement).value;
          startDrill(id);
        });
        return b;
      })(),
    ]),
    el("p", { class: "legal-note", style: "margin-top:0.75rem" }, [
      "Study a slice via URL: ",
      el("code", { text: `?drill=${sampleSub}` }),
      " (subelement) or ",
      el("code", { text: `?group=${sampleGroup}` }),
      ". Class: ",
      el("code", { text: "?class=tech|gen|extra" }),
      ".",
    ]),
  ]);

  modes.append(mockCard, drillCard);

  const tools = el("section", { class: "card ttt-card", style: "margin-top:1rem" }, [
    el("div", { class: "kicker", text: "Local progress" }),
    el("h2", { text: "Export" }),
    el("p", { class: "legal-note" }, [
      "Attempts are saved in IndexedDB (",
      el("code", { text: "gota-ticket-to-talk" }),
      "), namespaced by pool/element so classes do not bleed. Export a JSON backup anytime.",
    ]),
    el("div", { class: "actions" }, [
      (() => {
        const b = el("button", { type: "button", class: "btn ghost", id: "ttt-export" }, [
          "Export LocalProgress JSON",
        ]);
        b.addEventListener("click", async () => {
          const data = await exportLocalProgress(pool!.poolVersion);
          downloadJson(
            `ticket-to-talk-progress-${new Date().toISOString().slice(0, 10)}.json`,
            data,
          );
        });
        return b;
      })(),
    ]),
  ]);

  const checklistLinks: HTMLElement[] =
    cfg.key === "technician"
      ? [
          el("li", {}, [
            el("a", { href: "/field/" }, ["Field sheet"]),
            document.createTextNode(" — print freqs / grid / bailout before you lose cell."),
          ]),
          el("li", {}, [
            el("a", { href: "/guide/start/first-activation/" }, ["First activation"]),
            document.createTextNode(" — after you pass: 146.52 / NAAF simplex path."),
          ]),
          el("li", {}, [
            el("a", { href: "/guide/#after-you-pass-fee-csce-and-when-you-may-transmit" }, [
              "Overview — after you pass",
            ]),
            document.createTextNode(" — fee, CSCE, when you may transmit."),
          ]),
        ]
      : [
          el("li", {}, [
            el("a", { href: "/guide/" }, ["Exam-prep guide"]),
            document.createTextNode(" — Overview and class study path."),
          ]),
          el("li", {}, [
            el("a", { href: "/guide/start/" }, ["Guide start"]),
            document.createTextNode(" — begin the licensed path."),
          ]),
          el("li", {}, [
            el("a", { href: "/field/" }, ["Field sheet"]),
            document.createTextNode(" — print freqs / grid / bailout before you lose cell."),
          ]),
        ];

  const checklist = el(
    "section",
    { class: "card ttt-card ttt-checklist", style: "margin-top:1rem" },
    [
      el("div", { class: "kicker", text: "Near exam day" }),
      el("h2", { text: "Exam day checklist" }),
      el("ul", { class: "ttt-checklist-list" }, checklistLinks),
    ],
  );

  root.append(banner, hero, modes, tools, checklist);
}

function startMock(): void {
  if (!pool) return;
  const draft = createMockAttempt(pool, seedNow());
  session = {
    mode: "full_mock",
    draft,
    pool,
    startedAt: new Date().toISOString(),
    index: 0,
    answers: {},
    hintUsed: false,
    hintUsedByQuestion: {},
    revealed: false,
    streak: 0,
    bestStreak: 0,
  };
  lastResult = null;
  renderQuestion();
}

function startDrill(subelement: string): void {
  if (!pool) return;
  const draft = createDrillAttempt(pool, { kind: "subelement", id: subelement }, seedNow());
  session = {
    mode: "section_drill",
    draft,
    pool,
    startedAt: new Date().toISOString(),
    index: 0,
    answers: {},
    hintUsed: false,
    hintUsedByQuestion: {},
    revealed: false,
    streak: 0,
    bestStreak: 0,
  };
  lastResult = null;
  renderQuestion();
}

function currentQuestion(): Question {
  const s = session!;
  const item = s.draft.items[s.index]!;
  const q = getQuestion(s.pool, item.questionId);
  if (!q) throw new Error(`Missing question ${item.questionId}`);
  return q;
}

function renderSessionChips(s: Session): HTMLElement {
  recomputeStreak(s);
  const { correct, answered } = sessionScore(s);
  const total = s.draft.items.length;
  const n = s.index + 1;
  const pct = Math.round((n / total) * 100);

  const bar = el("div", {
    class: "ttt-progress-bar",
    role: "progressbar",
    "aria-valuemin": "0",
    "aria-valuemax": "100",
    "aria-valuenow": String(pct),
    "aria-label": `Question ${n} of ${total}`,
  });
  const fill = el("div", { class: "ttt-progress-fill" });
  fill.style.width = `${pct}%`;
  bar.append(fill);

  const row = el("div", { class: "ttt-session-meta" }, [
    el("div", { class: "ttt-progress", role: "status", "aria-live": "polite" }, [
      `${s.mode === "full_mock" ? "Full mock" : "Section drill"} · Question ${n} of ${total} · ${pct}%`,
    ]),
    el("div", { class: "ttt-game-chips", "aria-live": "polite" }, [
      el("span", {
        class: "ttt-chip ttt-streak-chip",
        text: `Streak ${s.streak}`,
        title: "Consecutive correct answers this session",
      }),
      el("span", {
        class: "ttt-chip ttt-score-chip",
        text: `${correct} / ${answered}`,
        title: "Correct / answered so far",
      }),
    ]),
    bar,
  ]);
  return row;
}

function renderQuestion(): void {
  const s = session!;
  const root = app();
  root.replaceChildren();

  const item = s.draft.items[s.index]!;
  const q = currentQuestion();
  const total = s.draft.items.length;
  const n = s.index + 1;
  const selected = s.answers[q.id];

  const meta = renderSessionChips(s);

  const stem = el("fieldset", { class: "card ttt-q" }, [
    el("legend", { class: "ttt-stem" }, [
      el("span", { class: "ttt-qid", text: q.id }),
      document.createTextNode(" "),
      q.text,
    ]),
  ]);

  if (q.diagram) {
    const fig = el("figure", { class: "ttt-diagram" });
    const img = document.createElement("img");
    img.src = `./diagrams/${q.diagram}.png`;
    img.alt = `Exam figure ${q.diagram} for question ${q.id}`;
    img.loading = "lazy";
    fig.append(img, el("figcaption", { class: "legal-note" }, [`Figure ${q.diagram}`]));
    stem.append(fig);
  }

  const list = el("div", {
    class: "ttt-choices",
    role: "radiogroup",
    "aria-label": "Answer choices",
  });

  item.choiceMap.forEach((letter, slot) => {
    const choiceText = q.choices[letter];
    const displayLetter = String.fromCharCode(65 + slot);
    const id = `choice-${slot}`;
    const label = el("label", { class: "ttt-choice", for: id });
    const input = el("input", {
      type: "radio",
      name: "ttt-answer",
      id,
      value: letter,
    }) as HTMLInputElement;
    input.checked = selected === letter;
    input.disabled = s.mode === "section_drill" && s.revealed;
    input.addEventListener("change", () => {
      s.answers[q.id] = letter;
      list.querySelectorAll(".ttt-choice").forEach((n) => n.classList.remove("is-selected"));
      label.classList.add("is-selected");
      // Mock: update chips without revealing which letter is correct
      if (s.mode === "full_mock") {
        const chips = root.querySelector(".ttt-session-meta");
        if (chips) chips.replaceWith(renderSessionChips(s));
      }
    });
    if (selected === letter) label.classList.add("is-selected");

    if (s.mode === "section_drill" && s.revealed) {
      if (letter === q.answer) {
        label.classList.add("is-correct", "ttt-flash-ok");
        label.append(
          input,
          el("span", { class: "ttt-choice-mark", "aria-hidden": "true", text: "✓ " }),
          el("span", {}, [`${displayLetter}. ${choiceText}`]),
          el("span", { class: "ttt-sr-feedback", text: " (correct answer)" }),
        );
      } else if (selected === letter) {
        label.classList.add("is-wrong", "ttt-flash-bad");
        label.append(
          input,
          el("span", { class: "ttt-choice-mark", "aria-hidden": "true", text: "✗ " }),
          el("span", {}, [`${displayLetter}. ${choiceText}`]),
          el("span", { class: "ttt-sr-feedback", text: " (your answer, incorrect)" }),
        );
      } else {
        label.append(input, el("span", {}, [`${displayLetter}. ${choiceText}`]));
      }
    } else {
      label.append(input, el("span", {}, [`${displayLetter}. ${choiceText}`]));
    }
    list.append(label);
  });

  stem.append(list);

  const actions = el("div", { class: "ttt-actions actions" });

  if (s.mode === "section_drill") {
    if (!s.revealed) {
      if (!s.hintUsed && s.draft.hintBudget > 0) {
        const hintBtn = el("button", { type: "button", class: "btn ghost", id: "ttt-hint" }, [
          "Retransmit hint (1 left)",
        ]);
        hintBtn.addEventListener("click", () => {
          s.hintUsed = true;
          s.hintUsedByQuestion[q.id] = true;
          const box = document.getElementById("ttt-hint-box");
          if (box) {
            box.hidden = false;
            box.textContent = q.hint;
          }
          hintBtn.disabled = true;
          hintBtn.textContent = "Hint used";
        });
        actions.append(hintBtn);
      }
      const checkBtn = el("button", { type: "button", class: "btn", id: "ttt-check" }, [
        "Check answer",
      ]);
      checkBtn.addEventListener("click", () => {
        if (!s.answers[q.id]) {
          window.alert("Pick a choice before checking.");
          return;
        }
        s.revealed = true;
        recomputeStreak(s);
        renderQuestion();
      });
      actions.append(checkBtn);
    } else {
      const nextBtn = el("button", { type: "button", class: "btn", id: "ttt-next" }, [
        n < total ? "Next question" : "See results",
      ]);
      nextBtn.addEventListener("click", () => {
        if (n < total) {
          s.index += 1;
          s.revealed = false;
          renderQuestion();
        } else {
          void finishSession();
        }
      });
      actions.append(nextBtn);
    }
  } else {
    const prev = el("button", { type: "button", class: "btn ghost", id: "ttt-prev" }, [
      "Previous",
    ]);
    (prev as HTMLButtonElement).disabled = s.index === 0;
    prev.addEventListener("click", () => {
      if (s.index > 0) {
        s.index -= 1;
        renderQuestion();
      }
    });
    const next = el("button", { type: "button", class: "btn ghost", id: "ttt-next" }, [
      n < total ? "Next" : "Review & submit",
    ]);
    next.addEventListener("click", () => {
      if (n < total) {
        s.index += 1;
        renderQuestion();
      } else {
        renderMockReview();
      }
    });
    actions.append(prev, next);
  }

  const abort = el("button", { type: "button", class: "btn ghost", id: "ttt-abort" }, [
    "Exit to home",
  ]);
  abort.addEventListener("click", () => {
    if (window.confirm("Leave this attempt? Progress for this run will not be saved.")) {
      session = null;
      void refreshHomeStats().then(() => renderHome());
    }
  });

  const hintBox = el("div", {
    id: "ttt-hint-box",
    class: "ttt-hint",
    role: "status",
    "aria-live": "polite",
  });
  (hintBox as HTMLDivElement).hidden = !s.hintUsedByQuestion[q.id];
  if (s.hintUsedByQuestion[q.id]) hintBox.textContent = q.hint;

  let feedback: HTMLElement | null = null;
  if (s.mode === "section_drill" && s.revealed) {
    const ok = s.answers[q.id] === q.answer;
    feedback = el(
      "div",
      {
        class: ok ? "ttt-feedback ok ttt-flash-ok" : "ttt-feedback bad ttt-flash-bad",
        role: "status",
        "aria-live": "assertive",
      },
      [
        el("strong", { text: ok ? "Solid copy." : "Not quite." }),
        document.createTextNode(" "),
        q.explanation,
      ],
    );
  }

  root.append(meta, stem);
  if (hintBox) root.append(hintBox);
  if (feedback) root.append(feedback);
  root.append(
    actions,
    el("div", { class: "ttt-actions actions", style: "margin-top:0.5rem" }, [abort]),
  );
}

function renderMockReview(): void {
  const s = session!;
  const root = app();
  root.replaceChildren();

  const blanks = s.draft.items.filter((i) => !s.answers[i.questionId]);
  const answered = s.draft.items.length - blanks.length;

  root.append(
    el("section", { class: "card" }, [
      el("div", { class: "kicker", text: "Full mock" }),
      el("h2", { text: "Ready to submit?" }),
      el("p", {}, [
        `Answered ${answered} of ${s.draft.items.length}.`,
        blanks.length
          ? ` ${blanks.length} blank${blanks.length === 1 ? "" : "s"} will count as incorrect.`
          : " All questions answered.",
      ]),
    ]),
  );

  const actions = el("div", { class: "actions ttt-actions" });
  const back = el("button", { type: "button", class: "btn ghost" }, ["Back to questions"]);
  back.addEventListener("click", () => renderQuestion());
  const submit = el("button", { type: "button", class: "btn" }, ["Submit mock"]);
  submit.addEventListener("click", () => {
    if (blanks.length > 0) {
      const ok = window.confirm(
        `You have ${blanks.length} unanswered question(s). Blanks count as incorrect. Submit anyway?`,
      );
      if (!ok) return;
    }
    void finishSession();
  });
  actions.append(back, submit);
  root.append(actions);
}

async function finishSession(): Promise<void> {
  const s = session!;
  const result = submitAttempt(s.draft, s.answers, s.pool, {
    startedAt: s.startedAt,
    finishedAt: new Date().toISOString(),
    hintUsedByQuestion: s.hintUsedByQuestion,
    sessionHintUsed: s.hintUsed,
  });
  lastResult = result;
  sessionOnlyStreak = Math.max(sessionOnlyStreak, s.bestStreak);
  try {
    await saveAttempt(result);
  } catch (err) {
    console.warn("IndexedDB save failed", err);
  }
  session = null;
  await refreshHomeStats();
  renderResults(result);
}

async function refreshHomeStats(): Promise<void> {
  if (!pool) return;
  try {
    homeStats = await computeHomeStats(pool.poolVersion, configForPool(pool).element);
  } catch {
    /* keep prior */
  }
}

function renderResults(result: AttemptResult): void {
  const root = app();
  root.replaceChildren();
  const cfg = pool ? configForPool(pool) : CLASS_CONFIGS[activeClass];
  const passLine = pool ? passScoreForPool(pool) : cfg.passScore;

  const passLabel =
    result.mode === "full_mock"
      ? result.passed
        ? "PASS"
        : "Not this time"
      : "Drill complete";

  const bannerClass =
    result.mode === "full_mock"
      ? result.passed
        ? "ttt-result pass"
        : "ttt-result fail"
      : "ttt-result drill";

  const bannerKids: (Node | string)[] = [
    el("div", {
      class: "kicker",
      text: result.mode === "full_mock" ? "Full mock result" : "Drill result",
    }),
  ];
  if (result.mode === "full_mock" && result.passed) {
    bannerKids.push(el("div", { class: "ttt-pass-badge", text: "PASS" }));
    bannerKids.push(
      el("h2", { text: "Ticket punched. Practice pass confirmed." }),
    );
  } else {
    bannerKids.push(el("h2", { text: passLabel }));
  }
  bannerKids.push(
    el("p", { class: "ttt-score" }, [
      el("strong", { text: `${result.score} / ${result.total}` }),
      document.createTextNode(
        result.mode === "full_mock"
          ? ` · need ${passLine} to pass`
          : result.hintUsed
            ? " · retransmit hint used"
            : " · hint unused",
      ),
    ]),
  );

  const banner = el("section", { class: `card ${bannerClass}`, role: "status" }, bannerKids);

  const misses = result.items.filter((i) => !i.isCorrect);
  const review = el("section", { class: "card", style: "margin-top:1rem" }, [
    el("div", { class: "kicker", text: "Review" }),
    el("h2", { text: misses.length ? "Misses & blanks" : "Clean copy" }),
  ]);

  if (misses.length === 0) {
    review.append(el("p", { text: "Every answer correct. Solid copy." }));
  } else {
    const ul = el("ul", { class: "ttt-miss-list" });
    for (const item of misses) {
      const q = getQuestion(pool!, item.questionId)!;
      const li = el("li", {}, [
        el("strong", { text: q.id }),
        document.createTextNode(
          ` — you: ${item.selected ?? "(blank)"}; correct: ${item.correct}. ${q.explanation}`,
        ),
      ]);
      ul.append(li);
    }
    review.append(ul);
  }

  const actions = el("div", { class: "actions ttt-actions", style: "margin-top:1rem" }, []);
  const home = el("button", { type: "button", class: "btn" }, ["Back to home"]);
  home.addEventListener("click", () => {
    lastResult = null;
    renderHome();
  });
  actions.append(home);

  const nodes: HTMLElement[] = [banner, review];

  if (result.mode === "full_mock") {
    const isTech = cfg.key === "technician";
    const nearMiss = !result.passed && result.score >= passLine - 4;

    if (result.passed) {
      const ordered =
        isTech
          ? el("ol", { class: "ttt-funnel-list" }, [
              el("li", {}, [
                el("strong", { text: "After you pass" }),
                document.createTextNode(" — fee, CSCE, when you may transmit. "),
                el(
                  "a",
                  {
                    href: "/guide/#after-you-pass-fee-csce-and-when-you-may-transmit",
                  },
                  ["Overview after you pass"],
                ),
              ]),
              el("li", {}, [
                el("strong", { text: "First net / Get licensed" }),
                document.createTextNode(" — "),
                el("a", { href: "/guide/#get-licensed-get-on-the-air" }, [
                  "Get licensed · get on the air",
                ]),
                document.createTextNode(" · "),
                el("a", { href: "/guide/start/" }, ["Start hub"]),
              ]),
              el("li", {}, [
                el("strong", { text: "First park day" }),
                document.createTextNode(" — "),
                el("a", { href: "/guide/start/first-activation/" }, [
                  "First activation",
                ]),
              ]),
              el("li", {}, [
                el("strong", { text: "Day-of tools" }),
                document.createTextNode(" — "),
                el("a", { href: "/field/" }, ["Field sheet"]),
                document.createTextNode(" · "),
                el("a", { href: "/plan/" }, ["Plan"]),
                document.createTextNode(" · "),
                el("a", { href: "/doctor/" }, ["Doctor"]),
              ]),
            ])
          : el("p", {}, [
              "Practice pass ≠ license upgrade. Review the exam-prep guide Overview and start path before exam week wraps.",
            ]);

      const ctaKids: (Node | string)[] = [
        el("div", { class: "kicker", text: "You punched the ticket" }),
        el("h2", { text: isTech ? "Next: get on the air" : "Next: keep building" }),
        el("p", {}, [
          isTech
            ? "Practice pass ≠ license. Ordered next steps — no Quest login required."
            : "Practice pass ≠ license upgrade. Keep the path simple.",
        ]),
        ordered,
      ];
      if (!isTech) {
        ctaKids.push(
          el("div", { class: "actions ttt-actions" }, [
            el("a", { class: "btn", href: "/guide/" }, ["Exam-prep guide"]),
            el("a", { class: "btn ghost", href: "/guide/start/" }, ["Guide start"]),
          ]),
        );
      }
      nodes.push(
        el("section", { class: "card ttt-card ttt-postpass ttt-funnel", style: "margin-top:1rem" }, ctaKids),
      );
    } else if (nearMiss) {
      const retry = el("button", { type: "button", class: "btn" }, ["Start full mock again"]);
      retry.addEventListener("click", () => {
        lastResult = null;
        startMock();
      });
      const drillHome = el("button", { type: "button", class: "btn ghost" }, ["Back to drill home"]);
      drillHome.addEventListener("click", () => {
        lastResult = null;
        renderHome();
      });
      nodes.push(
        el("section", { class: "card ttt-card ttt-nearmiss", style: "margin-top:1rem" }, [
          el("div", { class: "kicker", text: "Close — keep drilling" }),
          el("h2", { text: "Almost there" }),
          el("p", {}, [
            `You are within ${passLine - result.score} of a practice pass (${passLine}/${result.total}). Drill weak sections, then run another full mock — no Quest login.`,
          ]),
          el("div", { class: "actions ttt-actions" }, [
            retry,
            drillHome,
            el("a", { class: "btn ghost", href: "/guide/#get-licensed-get-on-the-air" }, [
              "Overview — get licensed",
            ]),
          ]),
        ]),
      );
    } else {
      const retry = el("button", { type: "button", class: "btn" }, ["Start full mock again"]);
      retry.addEventListener("click", () => {
        lastResult = null;
        startMock();
      });
      nodes.push(
        el("section", { class: "card ttt-card ttt-encourage", style: "margin-top:1rem" }, [
          el("div", { class: "kicker", text: "Keep going" }),
          el("h2", { text: "More reps, then another mock" }),
          el("p", {}, [
            "Misses above show where to drill. Use the Overview exam-prep path, then start a full mock again when ready.",
          ]),
          el("div", { class: "actions ttt-actions" }, [
            el("a", { class: "btn", href: "/guide/#get-licensed-get-on-the-air" }, [
              "Overview — exam prep",
            ]),
            retry,
          ]),
        ]),
      );
    }
  }

  nodes.push(actions);
  root.append(...nodes);
}

function renderError(msg: string): void {
  const root = app();
  root.replaceChildren(
    el("section", { class: "card" }, [
      el("h2", { text: "Pool pack not installed" }),
      el("p", { text: msg }),
      makeClassPicker((key) => {
        void switchClass(key);
      }),
    ]),
  );
}

function resolveInitialClass(): ClassKey {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = parseClassKey(params.get("class"));
  if (fromQuery) return fromQuery;
  const stored = readStoredClass();
  if (stored) return stored;
  return "technician";
}

function applyDeepLink(): void {
  const params = new URLSearchParams(window.location.search);
  const drill = (params.get("drill") || params.get("sub") || "").toUpperCase();
  const group = (params.get("group") || "").toUpperCase();
  const activeGroups = pool ? activeGroupsInPool(pool) : new Set<string>();

  if (group && /^[TGE][0-9][A-Z]$/.test(group) && activeGroups.has(group)) {
    if (!pool) return;
    const draft = createDrillAttempt(pool, { kind: "group", id: group }, seedNow());
    session = {
      mode: "section_drill",
      draft,
      pool,
      startedAt: new Date().toISOString(),
      index: 0,
      answers: {},
      hintUsed: false,
      hintUsedByQuestion: {},
      revealed: false,
      streak: 0,
      bestStreak: 0,
    };
    lastResult = null;
    history.replaceState({}, "", window.location.pathname);
    renderQuestion();
    return;
  }

  if (drill && /^[TGE][0-9]$/.test(drill)) {
    const subs = pool ? subelementsFromPool(pool) : [];
    if (subs.includes(drill)) {
      const sel = document.getElementById("ttt-subelement") as HTMLSelectElement | null;
      if (sel) sel.value = drill;
      startDrill(drill);
      history.replaceState({}, "", window.location.pathname);
    }
  }
}

async function boot(): Promise<void> {
  try {
    activeClass = resolveInitialClass();
    try {
      localStorage.setItem(CLASS_STORAGE_KEY, activeClass);
    } catch {
      /* ignore */
    }
    pool = await loadPool(activeClass);
    await ensureProfile(pool.poolVersion, activeClass);
    homeStats = await computeHomeStats(
      pool.poolVersion,
      CLASS_CONFIGS[activeClass].element,
    ).catch(() => null);
    updateDocMeta(pool, activeClass);
    renderHome();
    applyDeepLink();
  } catch (err) {
    renderError(err instanceof Error ? err.message : String(err));
  }
}

void boot();
